/**
 * Детальная страница рассылки `/broadcasts/{id}`.
 *
 * Композиция:
 *   1. Header — название + статус-тег + actions (Старт / Стоп / Дубликат / Удалить)
 *   2. KPI-карточки — Recipients / Sent / Blocked / Failed / Pending + Progress bar
 *   3. Timeline — created_at → started_at → finished_at + длительность
 *   4. Audience — сегмент (тип + размер)
 *   5. Content preview — рендер HTML так как видит юзер в TG
 *   6. Recipients table — список получателей с per-user статусом доставки,
 *      фильтры (статус, поиск), пагинация
 *
 * Auto-refresh каждые 5с пока status ∈ (queued, running) — прогресс крутится
 * без F5. После completed — больше не дёргает API.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router";
import { useShow, useInvalidate } from "@refinedev/core";
import { useTable } from "@refinedev/antd";
import {
  Card, Row, Col, Space, Button, Tag, Typography, Progress, Statistic,
  Descriptions, Table, Input, Select, Tooltip, Skeleton, App as AntApp,
  Result, Empty, Alert,
} from "antd";
import {
  PlayCircleOutlined, StopOutlined, CopyOutlined, DeleteOutlined,
  ArrowLeftOutlined, ReloadOutlined, UserOutlined, CheckCircleOutlined,
  CloseCircleOutlined, WarningOutlined, ClockCircleOutlined,
  SearchOutlined, MailOutlined, BlockOutlined,
} from "@ant-design/icons";
import { API_URL } from "../../dataProvider";
import { fmtDateTime } from "../../utils";

const STATUS_META: Record<string, { color: string; label: string; icon: any }> = {
  draft:     { color: "default",    label: "Черновик",  icon: <ClockCircleOutlined /> },
  queued:    { color: "blue",       label: "В очереди", icon: <ClockCircleOutlined /> },
  running:   { color: "processing", label: "Идёт",      icon: <ReloadOutlined spin /> },
  completed: { color: "success",    label: "Готово",    icon: <CheckCircleOutlined /> },
  cancelled: { color: "default",    label: "Отменена",  icon: <CloseCircleOutlined /> },
  failed:    { color: "error",      label: "Ошибка",    icon: <WarningOutlined /> },
};

const RECIPIENT_STATUS_META: Record<string, { color: string; label: string }> = {
  pending: { color: "default",    label: "В очереди" },
  sent:    { color: "success",    label: "Доставлено" },
  blocked: { color: "warning",    label: "Бот заблокирован" },
  failed:  { color: "error",      label: "Ошибка" },
};

interface Broadcast {
  id: number;
  name: string;
  status: string;
  segment_filter: { type?: string; user_ids?: number[]; tag_id?: number };
  content_blocks: Array<{ type: string; html?: string; text?: string; caption?: string; file_id?: string }>;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
  created_by: number | null;
  recipients_count: number;
  sent_count: number;
  blocked_count: number;
  error_count: number;
  converted_count?: number;
  revenue_rub?: number;
}

interface Recipient {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  tg_username: string | null;
  status: string;
  sent_at: string | null;
  error: string | null;
  clicked_at: string | null;
  converted_payment_id: number | null;
}

/** Длительность рассылки в человеко-читаемом виде */
const fmtDuration = (start: string | null, end: string | null): string => {
  if (!start || !end) return "—";
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return "<1 сек";
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} сек`;
  if (ms < 3600_000) return `${(ms / 60_000).toFixed(1)} мин`;
  return `${(ms / 3600_000).toFixed(1)} ч`;
};

export const BroadcastShow = () => {
  const { id } = useParams<{ id: string }>();
  const broadcastId = Number(id);
  const nav = useNavigate();
  const invalidate = useInvalidate();
  const { modal, message } = AntApp.useApp();
  const [acting, setActing] = useState(false);

  // Auto-refresh пока broадcast в queued/running. Когда terminal — стоп.
  // useShow() без аргументов — берёт resource и id из URL/контекста Refine.
  // Это идиоматичный путь для v5 (см. также pages/subscriptions/show.tsx).
  const { query } = useShow<Broadcast>({
    queryOptions: {
      refetchInterval: ((q: any) => {
        const status = q?.state?.data?.data?.status;
        return ["queued", "running"].includes(status || "") ? 5_000 : false;
      }) as any,
    },
  });
  const data = query?.data?.data;
  const meta = data ? (STATUS_META[data.status] || STATUS_META.draft) : null;

  // ────── Таблица получателей (отдельный запрос с фильтрами) ──────
  const { tableProps: recipientsProps, setFilters: setRecFilters, filters: recFilters }
    = useTable<Recipient>({
      resource: `broadcasts/${broadcastId}/recipients`,
      syncWithLocation: false,
      pagination: { mode: "server", pageSize: 50 },
      sorters: { initial: [{ field: "user_id", order: "asc" }] },
      queryOptions: {
        enabled: !!data,
        refetchInterval: data && ["queued", "running"].includes(data.status) ? 5_000 : false,
      },
    });

  const recQFilter = recFilters.find(f => "field" in f && f.field === "q");
  const recStatusFilter = recFilters.find(f => "field" in f && f.field === "status");
  const [recQInput, setRecQInput] = useState<string>(
    (recQFilter && "value" in recQFilter ? String(recQFilter.value) : "") || ""
  );
  useEffect(() => {
    const t = setTimeout(() => {
      const cleaned = recQInput.trim();
      const currQ = (recQFilter && "value" in recQFilter ? String(recQFilter.value) : "") || "";
      if (cleaned === currQ) return;
      setRecFilters([
        ...recFilters.filter(f => "field" in f && f.field !== "q"),
        ...(cleaned ? [{ field: "q", operator: "eq" as const, value: cleaned }] : []),
      ], "replace");
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recQInput]);

  const refresh = () => {
    invalidate({ resource: "broadcasts", id: broadcastId, invalidates: ["detail"] });
    invalidate({ resource: `broadcasts/${broadcastId}/recipients`, invalidates: ["list"] });
  };

  // ────── Actions: старт / стоп / дубликат / удалить ──────
  const act = async (path: string, okMsg: string) => {
    setActing(true);
    try {
      const r = await fetch(`${API_URL}/admin/broadcasts/${broadcastId}/${path}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: "{}",
      });
      if (r.ok) { message.success(okMsg); refresh(); }
      else {
        const t = await r.text().catch(() => r.statusText);
        message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
      }
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setActing(false);
    }
  };

  const onStart = () =>
    modal.confirm({
      title: `🚀 Запустить рассылку «${data?.name}»?`,
      content: (
        <div>
          <p>Рассылка будет отправлена <b>{data?.recipients_count || "—"}</b> получателям.</p>
          <p>Отменить можно только пока часть ещё не отправлена.</p>
        </div>
      ),
      okText: "Запустить", okButtonProps: { type: "primary" },
      onOk: async () => {
        await act("compile", "Аудитория собрана");
        await act("start", "Запущена");
      },
    });

  const onCancel = () =>
    modal.confirm({
      title: `⏹ Остановить рассылку «${data?.name}»?`,
      content: "Уже отправленные сообщения отозвать нельзя. Остановим только ещё-неотправленные.",
      okText: "Остановить", okButtonProps: { danger: true },
      onOk: () => act("cancel", "Рассылка остановлена"),
    });

  const onDuplicate = async () => {
    if (!data) return;
    setActing(true);
    try {
      const r = await fetch(`${API_URL}/admin/broadcasts`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `Copy of ${data.name}`,
          segment_filter: data.segment_filter,
          content_blocks: data.content_blocks,
        }),
      });
      if (r.ok) {
        const j = await r.json();
        message.success("Дубликат создан");
        nav(`/broadcasts/${j.id}`);
      } else {
        const t = await r.text();
        message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
      }
    } finally {
      setActing(false);
    }
  };

  const onDelete = () =>
    modal.confirm({
      title: `🗑 Удалить рассылку «${data?.name}»?`,
      content: "Это действие необратимо. Удаляется кампания + все записи получателей.",
      okText: "Удалить", okButtonProps: { danger: true },
      onOk: async () => {
        const r = await fetch(`${API_URL}/admin/broadcasts/${broadcastId}`, {
          method: "DELETE", credentials: "include",
        });
        if (r.ok) {
          message.success("Удалено");
          nav("/broadcasts");
        } else {
          const t = await r.text();
          message.error(`Ошибка ${r.status}: ${t.slice(0, 200)}`);
        }
      },
    });

  // ────── Загрузка / 404 / ошибки ──────
  if (query?.isLoading) {
    return <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>;
  }
  if (query?.isError) {
    const e: any = query?.error;
    if (e?.statusCode === 404) {
      return (
        <Result status="404" title="Рассылка не найдена"
          subTitle={`Кампания #${broadcastId} не существует или была удалена.`}
          extra={<Button type="primary" onClick={() => nav("/broadcasts")}>
            ← К списку рассылок
          </Button>} />
      );
    }
    return (
      <Result status="error" title="Не удалось загрузить рассылку"
        subTitle={String(e?.message || e)}
        extra={<Button onClick={() => query?.refetch()}>Повторить</Button>} />
    );
  }
  if (!data || !meta) {
    return <Empty description="Нет данных" />;
  }

  // ────── KPI ──────
  const total = data.recipients_count || 0;
  const done = data.sent_count + data.blocked_count + data.error_count;
  const pending = Math.max(0, total - done);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const isTerminal = ["completed", "cancelled", "failed"].includes(data.status);
  const isLive = ["queued", "running"].includes(data.status);

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>
      {/* Header */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12,
        alignItems: "center", justifyContent: "space-between",
        marginBottom: 16,
      }}>
        <Space size={12} wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => nav("/broadcasts")}>
            К списку
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {data.name}
          </Typography.Title>
          <Tag color={meta.color} icon={meta.icon} style={{ fontSize: 14, padding: "2px 10px" }}>
            {meta.label}
          </Tag>
        </Space>
        <Space size={6} wrap>
          <Tooltip title="Обновить данные">
            <Button icon={<ReloadOutlined />} onClick={refresh} />
          </Tooltip>
          {data.status === "draft" && (
            <Button type="primary" icon={<PlayCircleOutlined />}
              loading={acting} onClick={onStart}>
              Запустить
            </Button>
          )}
          {isLive && (
            <Button danger icon={<StopOutlined />}
              loading={acting} onClick={onCancel}>
              Остановить
            </Button>
          )}
          <Button icon={<CopyOutlined />} loading={acting} onClick={onDuplicate}>
            Дублировать
          </Button>
          {(data.status === "draft" || data.status === "cancelled") && (
            <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
              Удалить
            </Button>
          )}
        </Space>
      </div>

      {/* Hint для live статуса */}
      {isLive && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }}
          message="Рассылка идёт в реальном времени"
          description="Данные обновляются автоматически каждые 5 секунд. Cron-worker отправляет до 30 сообщений в секунду." />
      )}

      {/* KPI cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="Аудитория" value={total} prefix={<UserOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ borderColor: "#52c41a33" }}>
            <Statistic title="Доставлено" value={data.sent_count}
              prefix={<MailOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ borderColor: "#faad1433" }}>
            <Statistic title="Заблокирован бот" value={data.blocked_count}
              prefix={<BlockOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ color: "#faad14" }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" style={{ borderColor: data.error_count > 0 ? "#ff4d4f33" : undefined }}>
            <Statistic title="Ошибки" value={data.error_count}
              prefix={<WarningOutlined style={{ color: data.error_count > 0 ? "#ff4d4f" : undefined }} />}
              valueStyle={{ color: data.error_count > 0 ? "#ff4d4f" : undefined }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <Statistic title="В очереди" value={pending}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: pending > 0 ? "#1677ff" : undefined }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small">
            <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)", marginBottom: 4 }}>
              Прогресс
            </div>
            <Progress percent={pct} size="small"
              status={data.status === "failed" ? "exception"
                    : pct >= 100 ? "success"
                    : isLive ? "active" : "normal"} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Left: Timeline + Audience */}
        <Col xs={24} lg={10}>
          <Card title="📅 Хронология" size="small" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Создана">
                {fmtDateTime(data.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Запланирована">
                {data.scheduled_at ? fmtDateTime(data.scheduled_at) : <Typography.Text type="secondary">—</Typography.Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Стартовала">
                {data.started_at ? fmtDateTime(data.started_at) : <Typography.Text type="secondary">—</Typography.Text>}
              </Descriptions.Item>
              <Descriptions.Item label="Завершилась">
                {data.finished_at ? fmtDateTime(data.finished_at) : <Typography.Text type="secondary">—</Typography.Text>}
              </Descriptions.Item>
              {data.started_at && data.finished_at && (
                <Descriptions.Item label="Длительность">
                  <Tag color="blue">{fmtDuration(data.started_at, data.finished_at)}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="🎯 Аудитория" size="small">
            <Descriptions column={1} size="small" colon>
              <Descriptions.Item label="Тип">
                {data.segment_filter?.type === "all"   && <Tag>Все юзеры (не заблокировавшие бота)</Tag>}
                {data.segment_filter?.type === "tag"   && <Tag color="purple">Метка #{data.segment_filter.tag_id}</Tag>}
                {data.segment_filter?.type === "users" && <Tag color="cyan">Список конкретных юзеров</Tag>}
                {!data.segment_filter?.type && <Tag>—</Tag>}
              </Descriptions.Item>
              {data.segment_filter?.type === "users" && (
                <Descriptions.Item label="Размер списка">
                  <Tag>{(data.segment_filter.user_ids?.length || 0)} получателей</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Скомпилировано">
                <Tag color={total > 0 ? "blue" : "default"}>{total}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* Right: Content Preview */}
        <Col xs={24} lg={14}>
          <Card title="📩 Превью сообщения" size="small">
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Так получатели видят сообщение в Telegram
              ({data.content_blocks?.length || 0} {data.content_blocks?.length === 1 ? "блок" : "блока"})
            </Typography.Text>
            <div style={{
              marginTop: 12,
              padding: 12,
              background: "#f5f7fa",
              borderRadius: 8,
              border: "1px solid #e0e0e0",
              maxHeight: 480,
              overflowY: "auto",
            }}>
              {(data.content_blocks || []).map((b, i) => (
                <div key={i} style={{
                  padding: "10px 14px",
                  background: "#fff",
                  borderRadius: 12,
                  marginBottom: 8,
                  maxWidth: "85%",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}>
                  {b.type === "html" && (
                    <div className="bemli-tg-preview"
                      dangerouslySetInnerHTML={{ __html: b.html || "" }} />
                  )}
                  {b.type === "text" && (
                    <div style={{ whiteSpace: "pre-wrap" }}>{b.text}</div>
                  )}
                  {b.type === "photo" && (
                    <div>
                      <Tag>📷 Фото</Tag>
                      {b.caption && <div style={{ marginTop: 6 }}
                        dangerouslySetInnerHTML={{ __html: b.caption }} />}
                    </div>
                  )}
                  {b.type === "video" && (
                    <div>
                      <Tag>🎬 Видео</Tag>
                      {b.caption && <div style={{ marginTop: 6 }}
                        dangerouslySetInnerHTML={{ __html: b.caption }} />}
                    </div>
                  )}
                </div>
              ))}
              {(!data.content_blocks || data.content_blocks.length === 0) && (
                <Empty description="Содержимое пустое" />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Recipients table */}
      <Card title={
        <Space>
          <span>👥 Получатели</span>
          <Tag>{total}</Tag>
        </Space>
      } size="small" style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 12 }} wrap>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#999" }} />}
            placeholder="Поиск: имя, @username, tg_id…"
            value={recQInput}
            onChange={e => setRecQInput(e.target.value)}
            style={{ width: 300 }}
          />
          <Select
            style={{ width: 200 }} placeholder="Все статусы" allowClear
            value={(recStatusFilter && "value" in recStatusFilter
                    ? recStatusFilter.value : undefined) as string | undefined}
            onChange={v => setRecFilters([
              ...recFilters.filter(f => "field" in f && f.field !== "status"),
              ...(v ? [{ field: "status", operator: "eq" as const, value: v }] : []),
            ], "replace")}
            options={[
              { value: "sent",    label: "✓ Доставлено" },
              { value: "blocked", label: "🚫 Бот заблокирован" },
              { value: "failed",  label: "⚠ Ошибка" },
              { value: "pending", label: "⏱ В очереди" },
            ]}
          />
        </Space>
        <Table<Recipient> {...recipientsProps} rowKey="user_id" size="small">
          <Table.Column<Recipient> dataIndex="user_id" title="TG ID" width={120}
            render={(v: number) => (
              <Link to={`/users/${v}`}>
                <Typography.Text code style={{ fontSize: 12 }}>{v}</Typography.Text>
              </Link>
            )} />
          <Table.Column<Recipient> title="Юзер"
            render={(_, r) => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "—";
              return (
                <Space size={4}>
                  <Link to={`/users/${r.user_id}`}>{name}</Link>
                  {r.tg_username && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      @{r.tg_username}
                    </Typography.Text>
                  )}
                </Space>
              );
            }} />
          <Table.Column<Recipient> dataIndex="status" title="Статус" width={180}
            render={(v: string) => {
              const m = RECIPIENT_STATUS_META[v] || { color: "default", label: v };
              return <Tag color={m.color}>{m.label}</Tag>;
            }} />
          <Table.Column<Recipient> dataIndex="sent_at" title="Отправлено" width={170}
            render={(v: string | null) => v
              ? <Typography.Text style={{ fontSize: 12 }}>{fmtDateTime(v)}</Typography.Text>
              : <Typography.Text type="secondary">—</Typography.Text>
            } />
          <Table.Column<Recipient> dataIndex="error" title="Ошибка"
            render={(v: string | null) => v
              ? <Tooltip title={v}>
                  <Typography.Text type="danger" style={{ fontSize: 12 }}
                    ellipsis={{ tooltip: v }}>
                    {v.slice(0, 60)}
                  </Typography.Text>
                </Tooltip>
              : <Typography.Text type="secondary">—</Typography.Text>
            } />
        </Table>
      </Card>
    </div>
  );
};
